import axios from 'axios'
import { jwtDecode } from 'jwt-decode'
import { API_CLIENT_URLS } from './api/client'
import { useUserStore } from '../stores'
import { clearSession } from '../utils/userSessions'
import { signOut } from 'firebase/auth'
import { getFirebaseAuth } from './firebaseService'

interface TokenResponse {
    accessToken: string
    expiresIn?: number // Optional, we'll use JWT exp field instead
}

interface DecodedToken {
    exp: number
    [key: string]: string | number | boolean | undefined
}

class TokenService {
    private refreshPromise: Promise<string> | null = null
    private tokenExpiration: number = 0

    constructor() {
        this.initializeFromExistingToken()
    }

    clearTokens() {
        clearSession()
        this.tokenExpiration = 0
        // Clear all auth state so the user is redirected to login
        const store = useUserStore.getState()
        store.setUser(null)
        store.setToken(null)
        store.setFirebaseUser(null)
        // Sign out Firebase so the auth listener cleans up
        signOut(getFirebaseAuth()).catch(() => {})
    }

    private initializeFromExistingToken() {
        const existingToken = localStorage.getItem('accessToken')
        if (existingToken) {
            try {
                const decoded = jwtDecode(existingToken) as DecodedToken
                this.tokenExpiration = decoded.exp * 1000
                console.log('[TokenService] Initialized from existing token:', {
                    expiration: new Date(this.tokenExpiration).toISOString(),
                    exp: decoded.exp,
                })
            } catch (error) {
                console.warn('[TokenService] Failed to decode existing token:', error)
                this.clearTokens()
            }
        }
    }

    private isTokenExpired(): boolean {
        // No token obtained yet — expired by definition, no warning needed
        if (this.tokenExpiration === 0) {
            return true
        }

        // Genuinely invalid value — worth logging
        if (isNaN(this.tokenExpiration) || this.tokenExpiration < 0) {
            console.warn('[TokenService] Invalid tokenExpiration, treating as expired:', {
                tokenExpiration: this.tokenExpiration,
                type: typeof this.tokenExpiration,
            })
            return true
        }

        return Date.now() >= this.tokenExpiration - 30000 // 30 sec buffer
    }

    async refreshToken(forceRefresh: boolean = false): Promise<string> {
        // Don't refresh if token is still valid (unless forced)
        if (!forceRefresh && !this.isTokenExpired()) {
            return localStorage.getItem('accessToken')!
        }

        // If refresh already in progress, return that promise
        if (this.refreshPromise) {
            return this.refreshPromise
        }

        this.refreshPromise = (async () => {
            try {
                const refreshToken = localStorage.getItem('refreshToken')
                if (!refreshToken) {
                    console.error('[TokenService] No refresh token available')
                    this.clearTokens()
                    throw new Error('No refresh token available')
                }

                // Validate refresh token before attempting refresh
                try {
                    const decoded = jwtDecode(refreshToken) as DecodedToken
                    const now = Date.now() / 1000

                    if (decoded.exp && decoded.exp <= now) {
                        console.error('[TokenService] Refresh token expired')
                        this.clearTokens()
                        throw new Error('Refresh token expired - please log in again')
                    }
                } catch (error) {
                    console.error('[TokenService] Invalid refresh token:', error)
                    this.clearTokens()
                    throw new Error('Invalid refresh token')
                }

                const response = await axios.post<TokenResponse>(
                    `${API_CLIENT_URLS.identityApiUrl}/auth/refresh`,
                    {},
                    {
                        headers: {
                            Authorization: `Bearer ${refreshToken}`,
                        },
                    }
                )

                console.log('[TokenService] Refresh response received:', {
                    status: response.status,
                    hasAccessToken: !!response.data.accessToken,
                    accessTokenLength: response.data.accessToken?.length,
                    expiresIn: response.data.expiresIn,
                    expiresInType: typeof response.data.expiresIn,
                })

                const { accessToken, expiresIn } = response.data

                // Validate expiresIn before using it
                if (expiresIn && typeof expiresIn !== 'number') {
                    console.error('[TokenService] Invalid expiresIn type:', {
                        expiresIn,
                        type: typeof expiresIn,
                    })
                    throw new Error(`Invalid expiresIn type: ${typeof expiresIn}`)
                }

                // Decode the JWT to get the actual expiration time
                let decodedToken: DecodedToken
                try {
                    decodedToken = jwtDecode(accessToken) as DecodedToken
                    console.log('[TokenService] Decoded JWT token:', {
                        exp: decodedToken.exp,
                        expDate: new Date(decodedToken.exp * 1000).toISOString(),
                    })
                } catch (decodeError) {
                    console.error('[TokenService] Failed to decode JWT token:', decodeError)
                    throw new Error('Invalid JWT token received')
                }

                localStorage.setItem('accessToken', accessToken)
                // Use JWT exp field (in seconds) converted to milliseconds
                this.tokenExpiration = decodedToken.exp * 1000

                console.log('[TokenService] Token refreshed successfully:', {
                    newExpiration: new Date(this.tokenExpiration).toISOString(),
                    jwtExp: decodedToken.exp,
                    expiresInSeconds: expiresIn || 'not provided',
                    calculatedExpiration: this.tokenExpiration,
                })
                return accessToken
            } catch (error) {
                console.error('[TokenService] Failed to refresh token:', error)
                // Clear tokens on refresh failure
                this.clearTokens()
                // Re-throw the error for upstream handling
                throw error
            } finally {
                this.refreshPromise = null
            }
        })()

        return this.refreshPromise
    }
}

export const tokenService = new TokenService()
